import { APIClientServices } from "./APIClientServices";

export interface MemberConsumptionDTO {
	memberId: string;
	memberName: string;
	workspaceId: string | null;
	workspaceName: string | null;
	teamId: string;
	teamName: string | null;
	seniorityLevel: string;
	tokensUsed: number;
	tokensBonus: number;
	tokenLimit: number | null;
	effectiveLimit: number | null;
	usagePct: number | null;
	tokenLimitReached: boolean;
	rateLimitPerMinute: number;
	rateLimitOverride: number | null;
	rateLimitCurrent: number;
	rateLimitRemaining: number;
	isActive: boolean;
	memberLibraryName: string | null;
	updatedAt: string;
}

export interface BackofficeDashboardDTO {
	timestamp: string;
	kpis: {
		workspaces: { active: number };
		teams: { active: number };
		members: { total: number; junior: number; pleno: number; senior: number };
		tasks: { today: number; last7d: number; total: number };
		tokens: { today: number; thisMonth: number; total: number };
		mcps: { active: number; oauthContracts: number };
		templates: { active: number };
	};
	charts: {
		tokensByDay: Array<{ date: string; tokens: number }>;
		topMembers: Array<{ memberId: string; memberName: string; tokensUsed: number; limit: number | null; pct: number | null }>;
		mcpUsage: Array<{ mcpSlug: string; count: number }>;
	};
	activity: {
		recentTasks: Array<{
			id: string;
			objective: string;
			teamId: string;
			createdAt: string;
			subtasksTotal: number;
			subtasksCompleted: number;
			subtasksFailed: number;
		}>;
		systemHealth: {
			queues: Array<{ name: string; counts: Record<string, number>; healthStatus: "ok" | "warning" | "degraded" }>;
			scheduler: { activeScheduled: number; nextDueAt: string | null };
		};
		membersNearLimit: Array<{ memberId: string; memberName: string; tokensUsed: number; limit: number; pct: number; teamId: string }>;
	};
}

// EK-331: Queue health DTO retornado por /v1/backoffice/queues
export interface QueueHealthSnapshot {
	queue: string;
	counts: Record<string, number>;
	stalledCount: number;
	healthStatus: "ok" | "warning" | "degraded";
	active: Array<{ id: string; name: string; attemptsMade: number; processedOn: number | null; elapsedMs: number | null; potentiallyStalled: boolean; data: any }>;
	failed: Array<{ id: string; name: string; attemptsMade: number; failedReason: string | null; finishedOn: number | null; data: any }>;
	delayed: Array<{ id: string; name: string; attemptsMade: number; scheduledFor: number; data: any }>;
	completedRecent: Array<{ id: string; name: string; finishedOn: number | null; durationMs: number | null }>;
	latency24h: { sampleSize: number; windowMs: number; avgMs: number; p50Ms: number; p95Ms: number; p99Ms: number; maxMs: number };
	dlqTopReasons: Array<{ reasonNormalized: string; count: number; sample: string | null; firstSeen: number | null; lastSeen: number | null; queueName: string }>;
}

export interface QueueHealthResponse {
	timestamp: string;
	environment: string;
	overallHealth: "ok" | "warning" | "degraded";
	stallThresholdMs: number;
	queues: QueueHealthSnapshot[];
}

export class BackofficeAdminServices {
	private static readonly BASE = "v1/backoffice";

	static async listMembersConsumption(): Promise<MemberConsumptionDTO[]> {
		return APIClientServices.get<MemberConsumptionDTO[]>(`${this.BASE}/members-consumption`);
	}

	static async resetMemberTokens(memberId: string): Promise<void> {
		return APIClientServices.post(`${this.BASE}/members/${memberId}/reset-tokens`, {});
	}

	static async getDashboard(): Promise<BackofficeDashboardDTO> {
		return APIClientServices.get<BackofficeDashboardDTO>(`${this.BASE}/dashboard`);
	}

	static async getQueueHealth(): Promise<QueueHealthResponse> {
		return APIClientServices.get<QueueHealthResponse>(`${this.BASE}/queues`);
	}

	static async listMcpAuditLog(filters: McpAuditLogFilters = {}): Promise<McpAuditLogResponse> {
		const params: Record<string, string | number | boolean> = {};
		if (filters.accountId) params.accountId = filters.accountId;
		if (filters.mcpSlug) params.mcpSlug = filters.mcpSlug;
		if (filters.toolName) params.toolName = filters.toolName;
		if (typeof filters.success === "boolean") params.success = filters.success;
		if (filters.dateFrom) params.dateFrom = filters.dateFrom;
		if (filters.dateTo) params.dateTo = filters.dateTo;
		if (filters.limit !== undefined) params.limit = filters.limit;
		if (filters.offset !== undefined) params.offset = filters.offset;
		return APIClientServices.get<McpAuditLogResponse>(`${this.BASE}/mcp-audit-log`, params);
	}

	static async listDeadLetterJobs(filters: DeadLetterJobsFilters = {}): Promise<DeadLetterJobsResponse> {
		const params: Record<string, string | number | boolean> = {};
		if (filters.queueName) params.queueName = filters.queueName;
		if (filters.jobName) params.jobName = filters.jobName;
		if (filters.accountId) params.accountId = filters.accountId;
		if (filters.unresolved) params.unresolved = true;
		if (filters.dateFrom) params.dateFrom = filters.dateFrom;
		if (filters.dateTo) params.dateTo = filters.dateTo;
		if (filters.limit !== undefined) params.limit = filters.limit;
		if (filters.offset !== undefined) params.offset = filters.offset;
		return APIClientServices.get<DeadLetterJobsResponse>(`${this.BASE}/dead-letter-jobs`, params);
	}

	// ── DevBot Proposals (plano v4 §3.3.4 + §3.3.5) ────────────────────

	static async listTemplateProposals(params: { status?: ProposalStatus; limit?: number; offset?: number } = {}): Promise<ProposalsListResponse<TemplateProposalDTO>> {
		return APIClientServices.get<ProposalsListResponse<TemplateProposalDTO>>(`${this.BASE}/template-proposals`, params as any);
	}

	static async getTemplateProposal(id: string): Promise<TemplateProposalDTO> {
		return APIClientServices.get<TemplateProposalDTO>(`${this.BASE}/template-proposals/${id}`);
	}

	static async createTemplateProposal(gap_description: string, triggered_context?: Record<string, any>): Promise<TemplateProposalDTO> {
		return APIClientServices.post<TemplateProposalDTO>(`${this.BASE}/template-proposals`, { gap_description, triggered_context });
	}

	static async approveTemplateProposal(id: string, body: { notes?: string; edited_payload?: Record<string, any> } = {}): Promise<{ proposal: TemplateProposalDTO; createdMemberLibraryId: string; mcpVinculosCount: number }> {
		return APIClientServices.post(`${this.BASE}/template-proposals/${id}/approve`, body);
	}

	static async rejectTemplateProposal(id: string, notes?: string): Promise<TemplateProposalDTO> {
		return APIClientServices.post<TemplateProposalDTO>(`${this.BASE}/template-proposals/${id}/reject`, { notes });
	}

	static async listMcpProposals(params: { status?: ProposalStatus; limit?: number; offset?: number } = {}): Promise<ProposalsListResponse<McpProposalDTO>> {
		return APIClientServices.get<ProposalsListResponse<McpProposalDTO>>(`${this.BASE}/mcp-proposals`, params as any);
	}

	static async getMcpProposal(id: string): Promise<McpProposalDTO> {
		return APIClientServices.get<McpProposalDTO>(`${this.BASE}/mcp-proposals/${id}`);
	}

	static async createMcpProposal(gap_description: string, triggered_context?: Record<string, any>): Promise<McpProposalDTO> {
		return APIClientServices.post<McpProposalDTO>(`${this.BASE}/mcp-proposals`, { gap_description, triggered_context });
	}

	static async approveMcpProposal(id: string, body: { notes?: string; edited_payload?: Record<string, any> } = {}): Promise<{ proposal: McpProposalDTO; createdMcpLibraryId: string; note: string }> {
		return APIClientServices.post(`${this.BASE}/mcp-proposals/${id}/approve`, body);
	}

	static async rejectMcpProposal(id: string, notes?: string): Promise<McpProposalDTO> {
		return APIClientServices.post<McpProposalDTO>(`${this.BASE}/mcp-proposals/${id}/reject`, { notes });
	}

	static async listCompensationLog(filters: CompensationLogFilters = {}): Promise<CompensationLogResponse> {
		const params: Record<string, string | number | boolean> = {};
		if (filters.operationName) params.operationName = filters.operationName;
		if (filters.accountId) params.accountId = filters.accountId;
		if (filters.status) params.status = filters.status;
		if (filters.dateFrom) params.dateFrom = filters.dateFrom;
		if (filters.dateTo) params.dateTo = filters.dateTo;
		if (filters.limit !== undefined) params.limit = filters.limit;
		if (filters.offset !== undefined) params.offset = filters.offset;
		return APIClientServices.get<CompensationLogResponse>(`${this.BASE}/compensation-log`, params);
	}
}

// EK-281 Compensation DTOs
export type CompensationStatus = "rolled_back" | "partial" | "rollback_failed";

export interface CompensationEntry {
	name: string;
	success: boolean;
	durationMs: number;
	error?: string | null;
}

export interface PublicCompensationLogDTO {
	id: string;
	operationName: string;
	accountId: string | null;
	workspaceId: string | null;
	status: CompensationStatus;
	triggerErrorMessage: string;
	triggerErrorStack: string | null;
	compensations: CompensationEntry[];
	compensationsTotal: number;
	compensationsOk: number;
	compensationsFailed: number;
	durationMs: number;
	metadata: Record<string, any> | null;
	createdAt: string;
}

export interface CompensationLogFilters {
	operationName?: string;
	accountId?: string;
	status?: CompensationStatus;
	dateFrom?: string;
	dateTo?: string;
	limit?: number;
	offset?: number;
}

export interface CompensationLogResponse {
	total: number;
	limit: number;
	offset: number;
	items: PublicCompensationLogDTO[];
}

// EK-280 DLQ DTOs
export interface PublicDeadLetterJobDTO {
	id: string;
	queueName: string;
	jobName: string;
	bullmqJobId: string;
	accountId: string | null;
	workspaceId: string | null;
	teamId: string | null;
	taskId: string | null;
	attemptsMade: number;
	attemptsMax: number;
	failedReason: string;
	errorStack: string | null;
	errorCode: string | null;
	jobData: Record<string, any>;
	jobCreatedAt: string | null;
	firstAttemptedAt: string | null;
	finalFailedAt: string;
	createdAt: string;
	resolvedAt: string | null;
	resolvedBy: string | null;
	resolutionNotes: string | null;
}

export interface DeadLetterJobsFilters {
	queueName?: string;
	jobName?: string;
	accountId?: string;
	unresolved?: boolean;
	dateFrom?: string;
	dateTo?: string;
	limit?: number;
	offset?: number;
}

export interface DeadLetterJobsResponse {
	total: number;
	limit: number;
	offset: number;
	items: PublicDeadLetterJobDTO[];
}

// EK-291 DTOs
export interface PublicMcpAuditLogDTO {
	id: string;
	requestId: string | null;
	accountId: string;
	workspaceId: string | null;
	teamId: string | null;
	memberId: string | null;
	contractedMcpId: string | null;
	mcpSlug: string;
	toolName: string;
	argsPreview: Record<string, any>;
	argsSizeBytes: number;
	argsTruncated: boolean;
	success: boolean;
	durationMs: number;
	errorMessage: string | null;
	errorCode: string | null;
	resultPreview: Record<string, any> | null;
	createdAt: string;
}

export interface McpAuditLogFilters {
	accountId?: string;
	mcpSlug?: string;
	toolName?: string;
	success?: boolean;
	dateFrom?: string;
	dateTo?: string;
	limit?: number;
	offset?: number;
}

export interface McpAuditLogResponse {
	total: number;
	limit: number;
	offset: number;
	items: PublicMcpAuditLogDTO[];
}

// ── DevBot Proposals DTOs ────────────────────────────────────────────

export type ProposalStatus = "pending_review" | "approved" | "rejected" | "superseded";
export type TemplateTriggerKind = "hire_member" | "assign_task" | "manual_backoffice";
export type McpTriggerKind = "missing_mcp_coverage" | "chat_request" | "manual_backoffice";

export interface ChecklistItem {
	label: string;
	checked: boolean;
	note?: string;
}
export interface ChecklistSection {
	items: ChecklistItem[];
	summary?: string;
}

export interface TemplateProposalDTO {
	id: string;
	triggeredBy: TemplateTriggerKind;
	triggeredByUserId: string | null;
	triggeredContext: Record<string, any>;
	proposedSlug: string;
	proposedName: string;
	proposedDescription: string;
	proposedSystemPrompt: string;
	proposedTier: number;
	proposedSeniorityConfig: Record<string, any>;
	proposedCompetencies: { hard_skills: string[]; soft_skills: string[]; tools: string[] };
	proposedMcpSlugs: string[];
	proposedUseCases: Array<{ title: string; context: string; expected_outcome: string }>;
	proposedDeliverables: string[];
	checklist: Record<string, ChecklistSection>;
	status: ProposalStatus;
	reviewedBy: string | null;
	reviewedAt: string | null;
	reviewNotes: string | null;
	editedPayload: Record<string, any> | null;
	createdMemberLibraryId: string | null;
	llmModel: string;
	llmInputTokens: number | null;
	llmOutputTokens: number | null;
	llmCostUsd: number | null;
	createdAt: string;
	updatedAt: string;
}

export interface McpProposalDTO {
	id: string;
	triggeredBy: McpTriggerKind;
	triggeredByUserId: string | null;
	triggeredContext: Record<string, any>;
	proposedSlug: string;
	proposedName: string;
	proposedDescription: string;
	proposedCategory: string;
	proposedIconUrl: string | null;
	proposedSystemPrompt: string;
	proposedOauthConfig: { provider: string; scopes: string[]; auth_url?: string; token_url?: string };
	proposedApiConfig: { base_url?: string; version?: string; docs_url?: string; rate_limit?: string };
	proposedTools: Array<{ name: string; description: string; path?: string; method?: string; confirmation_required?: boolean }>;
	proposedToolEnvFields: any[];
	proposedPricing: Record<string, any>;
	specAnalysis: {
		api_availability?: string;
		oauth_complexity?: string;
		mcp_server_estimated_loc?: number;
		dependencies?: string[];
		risks?: string[];
		alternatives_considered?: string[];
	};
	status: ProposalStatus;
	reviewedBy: string | null;
	reviewedAt: string | null;
	reviewNotes: string | null;
	editedPayload: Record<string, any> | null;
	createdMcpLibraryId: string | null;
	llmModel: string;
	llmInputTokens: number | null;
	llmOutputTokens: number | null;
	llmCostUsd: number | null;
	createdAt: string;
	updatedAt: string;
}

export interface ProposalsListResponse<T> {
	items: T[];
	count: number;
	filters: { status: ProposalStatus; limit: number; offset: number };
}
